import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, Calendar, CheckCircle, TrendingUp, Truck, RotateCcw, FileText, XCircle, IndianRupee, Box, Scale, Ban } from "lucide-react";
import IndiaDeliveryMap from "@/components/IndiaDeliveryMap";

const SellerDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cityData, setCityData] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchCityData();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/seller/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCityData = async () => {
    try {
      const response = await axios.get(`${API}/seller/dashboard/city-delivery-stats`);
      setCityData(response.data);
    } catch (error) {
      console.error("Failed to fetch city data", error);
    }
  };

  const total = stats?.total_orders || 0;
  const pct = (val) => total > 0 ? `${((val / total) * 100).toFixed(1)}%` : "0%";

  const statCards = [
    { title: "Total Orders", value: stats?.total_orders || 0, icon: Package, color: "bg-blue-500", testId: "total-orders-stat" },
    { title: "New Orders", value: stats?.new_orders || 0, icon: FileText, color: "bg-cyan-500", testId: "new-orders-stat" },
    { title: "In-Process", value: stats?.in_transit_orders || 0, icon: Truck, color: "bg-orange-500", testId: "in-transit-stat", pct: pct(stats?.in_transit_orders || 0) },
    { title: "Completed Deliveries", value: stats?.completed_deliveries || 0, icon: CheckCircle, color: "bg-green-500", testId: "completed-deliveries-stat", pct: pct(stats?.completed_deliveries || 0) },
    { title: "Return Orders", value: stats?.return_orders || 0, icon: RotateCcw, color: "bg-red-500", testId: "return-orders-stat", pct: pct(stats?.return_orders || 0) },
    { title: "Not Picked", value: stats?.not_picked_orders || 0, icon: Ban, color: "bg-slate-500", testId: "not-picked-stat", pct: pct(stats?.not_picked_orders || 0) },
    { title: "Cancelled", value: stats?.cancelled_orders || 0, icon: XCircle, color: "bg-gray-500", testId: "cancelled-orders-stat", pct: pct(stats?.cancelled_orders || 0) },
    { title: "Today's Orders", value: stats?.today_orders || 0, icon: TrendingUp, color: "bg-purple-500", testId: "today-orders-stat", pct: pct(stats?.today_orders || 0) },
    { title: "With Appointments", value: stats?.total_appointments || 0, icon: Calendar, color: "bg-indigo-500", testId: "total-appointments-stat" },
  ];

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Dashboard</h1>
          <p className="text-slate-600">Welcome back, {user.name}!</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(7)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="pb-3"><div className="h-4 bg-slate-200 rounded w-24"></div></CardHeader>
                <CardContent><div className="h-8 bg-slate-200 rounded w-16"></div></CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statCards.map((stat, index) => (
                <Card key={index} data-testid={stat.testId} className="border-0 shadow-lg overflow-hidden relative transition-all hover:shadow-xl">
                  <div className={`absolute top-0 left-0 w-1 h-full ${stat.color}`}></div>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">{stat.title}</CardTitle>
                    <div className={`p-2 rounded-lg ${stat.color} bg-opacity-10`}>
                      <stat.icon className={`w-5 h-5 ${stat.color.replace('bg-', 'text-')}`} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-800">{stat.value}</div>
                    {stat.pct && <p className="text-xs text-slate-500 mt-1">{stat.pct} of total</p>}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Delivered Summary - 3 cards in one line */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-0 shadow-lg overflow-hidden relative" data-testid="total-delivered-invoice-stat">
                <div className="absolute top-0 left-0 w-1 h-full bg-emerald-600"></div>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Invoice Amount Delivered</CardTitle>
                  <div className="p-2 rounded-lg bg-emerald-600 bg-opacity-10"><IndianRupee className="w-5 h-5 text-emerald-600" /></div>
                </CardHeader>
                <CardContent><div className="text-3xl font-bold text-slate-800">₹{(stats?.total_delivered_invoice_amount || 0).toLocaleString()}</div></CardContent>
              </Card>
              <Card className="border-0 shadow-lg overflow-hidden relative" data-testid="total-delivered-boxes-stat">
                <div className="absolute top-0 left-0 w-1 h-full bg-amber-500"></div>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Boxes Delivered</CardTitle>
                  <div className="p-2 rounded-lg bg-amber-500 bg-opacity-10"><Box className="w-5 h-5 text-amber-500" /></div>
                </CardHeader>
                <CardContent><div className="text-3xl font-bold text-slate-800">{(stats?.total_delivered_boxes || 0).toLocaleString()}</div></CardContent>
              </Card>
              <Card className="border-0 shadow-lg overflow-hidden relative" data-testid="total-delivered-weight-stat">
                <div className="absolute top-0 left-0 w-1 h-full bg-sky-500"></div>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Weight (Kg) Delivered</CardTitle>
                  <div className="p-2 rounded-lg bg-sky-500 bg-opacity-10"><Scale className="w-5 h-5 text-sky-500" /></div>
                </CardHeader>
                <CardContent><div className="text-3xl font-bold text-slate-800">{(stats?.total_delivered_weight_kg || 0).toLocaleString()} kg</div></CardContent>
              </Card>
            </div>

            {/* India Map */}
            <IndiaDeliveryMap cityData={cityData} />

            {/* Quick Actions */}
            <Card className="border-0 shadow-lg">
              <CardHeader><CardTitle className="text-xl">Quick Actions</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <a href="/orders/create" data-testid="quick-action-create-order" className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl hover:shadow-lg transition-all border border-blue-100">
                    <Package className="w-8 h-8 text-blue-600 mb-3" />
                    <h3 className="font-semibold text-slate-800 mb-1">Create New Order</h3>
                    <p className="text-sm text-slate-600">Add a new delivery order</p>
                  </a>
                  <a href="/appointments" data-testid="quick-action-book-appointment" className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl hover:shadow-lg transition-all border border-indigo-100">
                    <Calendar className="w-8 h-8 text-indigo-600 mb-3" />
                    <h3 className="font-semibold text-slate-800 mb-1">View Appointments</h3>
                    <p className="text-sm text-slate-600">Manage delivery schedules</p>
                  </a>
                  <a href="/seller/orders" data-testid="quick-action-view-orders" className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl hover:shadow-lg transition-all border border-green-100">
                    <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                    <h3 className="font-semibold text-slate-800 mb-1">View All Orders</h3>
                    <p className="text-sm text-slate-600">Track your deliveries</p>
                  </a>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </SellerLayout>
  );
};

export default SellerDashboard;
